/**
 * Module-level multi-session manager for the P2P chat feature.
 *
 * Owns the live WebRTC {@link Session} closures (one per active chat), keyed by
 * the store's session id. Because it lives at module scope — NOT inside a React
 * component — backgrounded chats stay connected while the user views a different
 * foreground chat or navigates away from `/chat`. A session ends only when the
 * local user closes it, the remote peer leaves, or the user logs out
 * (`closeAll`); component unmounts do NOT tear sessions down.
 *
 * Data flow is strictly one-way: manager → store. The manager pushes connection
 * status, incoming messages, and errors into {@link useP2PChatStore} via
 * `getState()`. The store never imports the manager (no circular dependency).
 *
 * Cryptographic keys never leave the `chat.ts` closure; the manager only holds
 * the opaque `Session` handle (send/close).
 *
 * @module features/p2p-chat/lib/sessionManager
 */

import { useP2PChatStore } from '../stores/p2pChatStore';
import type { Session } from '../types/p2pChat.types';
import { startSession } from './chat';
import { getSignalUrls } from './signalUrls';

interface StartOptions {
  readonly id: string;
  readonly roomCode: string;
  readonly initiator: boolean;
  readonly relayOnly?: boolean;
}

interface ManagedSession {
  /** Resolved `Session` handle once bootstrap completes; null until then. */
  session: Session | null;
  /**
   * Set true when `close` is called before bootstrap resolves, so the deferred
   * `startSession` promise tears itself down instead of leaving a half-open
   * socket occupying one of the room's 2 peer slots.
   */
  cancelled: boolean;
  bootstrapTimer: ReturnType<typeof setTimeout> | null;
}

const sessions = new Map<string, ManagedSession>();

/**
 * Start a new WebRTC session for an already-registered store session id.
 *
 * Mirrors the StrictMode-safe bootstrap from the retired `useP2PSession` hook:
 * the actual `startSession` call is deferred by one task so a rapid
 * start→close cannot occupy a signaling peer slot. Callbacks write directly
 * into the store keyed by `id`.
 */
export function start(opts: StartOptions): void {
  const { id, roomCode, initiator, relayOnly } = opts;
  if (sessions.has(id)) return;

  const entry: ManagedSession = {
    session: null,
    cancelled: false,
    bootstrapTimer: null,
  };
  sessions.set(id, entry);

  const store = (): ReturnType<typeof useP2PChatStore.getState> =>
    useP2PChatStore.getState();

  entry.bootstrapTimer = setTimeout(() => {
    if (entry.cancelled) return;

    const { http, ws } = getSignalUrls();

    void startSession({
      roomCode,
      initiator,
      signalHttpUrl: http,
      signalWsUrl: ws,
      ...(relayOnly !== undefined ? { relayOnly } : {}),
      callbacks: {
        onMessage: (text) => {
          if (entry.cancelled) return;
          store().appendMessage(id, {
            id: crypto.randomUUID(),
            text,
            mine: false,
            timestamp: Date.now(),
          });
          // Only count toward unread when this chat is NOT in the foreground.
          if (store().foregroundId !== id) {
            store().incrementUnread(id);
          }
        },
        onStatusChange: (next) => {
          if (entry.cancelled) return;
          store().setStatus(id, next);
        },
        onError: (err) => {
          if (entry.cancelled) return;
          store().setError(id, err.message);
        },
      },
    })
      .then((session) => {
        if (entry.cancelled) {
          session.close();
          return;
        }
        entry.session = session;
      })
      .catch((err: unknown) => {
        if (entry.cancelled) return;
        store().setError(id, err instanceof Error ? err.message : String(err));
        store().setStatus(id, 'failed');
      });
  }, 0);
}

/**
 * Send a message on a session. Appends the local `mine: true` echo to the store
 * on success. Returns false when the session is missing or not connected.
 */
export function send(id: string, text: string): boolean {
  const entry = sessions.get(id);
  if (!entry || !entry.session) return false;
  const ok = entry.session.send(text);
  if (ok) {
    useP2PChatStore.getState().appendMessage(id, {
      id: crypto.randomUUID(),
      text,
      mine: true,
      timestamp: Date.now(),
    });
  }
  return ok;
}

/** Tear down a single session and forget it. Safe to call before bootstrap. */
export function close(id: string): void {
  const entry = sessions.get(id);
  if (!entry) return;
  entry.cancelled = true;
  if (entry.bootstrapTimer !== null) clearTimeout(entry.bootstrapTimer);
  entry.session?.close();
  sessions.delete(id);
}

/** Tear down every session (used on logout). */
export function closeAll(): void {
  for (const id of [...sessions.keys()]) close(id);
}

/** Whether a managed session with this id exists. */
export function has(id: string): boolean {
  return sessions.has(id);
}
